import { ChangeEvent, ReactNode } from "react";
import { ViewItem } from "../../../../Components/Views/ViewItem";
import { controllers } from "../../../../Controllers/Controllers";
import { navigator } from "../../../../Controllers/Navigator";
import { LeadCategoryType } from "../../../../DataTypes/leads";
import { EmailTemplateType } from "../../../../DataTypes/templates";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import './templates-styles.css'

type TemplatesPageState = {
    templates: EmailTemplateType[];
    categories: LeadCategoryType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    categoryFilter: string;
    deletingId: number | null;
}

class TemplatesPage extends ViewItem {
    state: TemplatesPageState;

    constructor(props: any) {
        super(props)
        this.state = {
            templates: [],
            categories: [],
            loading: true,
            error: null,
            needsAuth: false,
            categoryFilter: '',
            deletingId: null,
        }
    }

    componentDidMount(): void {
        this.loadTemplates()
        this.loadCategories()
    }

    loadCategories = async () => {
        const response = await controllers().outreach.getCategories()
        if (response && response.statusCode === 200) {
            this.setState({ categories: response.data?.categories || [] })
        }
    }

    loadTemplates = async () => {
        const { categoryFilter } = this.state
        this.setState({ loading: true, error: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }
        const response = await controllers().mail.getTemplates({
            category_id: categoryFilter || undefined,
        })
        if (!response || response.statusCode !== 200) {
            this.setState({ loading: false, error: response?.error || 'Failed to load templates.' })
            return
        }
        this.setState({ templates: response.data?.templates || [], loading: false })
    }

    handleCategoryFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ categoryFilter: e.target.value }, this.loadTemplates)
    }

    canManage = (template: EmailTemplateType): boolean => {
        return controllers().auth.isAdmin || template.owner?.id === controllers().auth.userInfo?.id
    }

    handleDelete = async (template: EmailTemplateType) => {
        const confirmed = window.confirm(`Delete template "${template.name}"?`)
        if (!confirmed) return

        this.setState({ deletingId: template.id })
        const response = await controllers().mail.deleteTemplate(template.id)
        this.setState({ deletingId: null })
        if (response && response.statusCode === 200) {
            this.loadTemplates()
        }
    }

    renderTable = () => {
        const { templates, deletingId } = this.state
        return (
            <div className="outreach-table-wrap">
                <table className="outreach-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Subject</th>
                            <th>Visibility</th>
                            <th>Owner</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {templates.map(template => (
                            <tr key={template.id}>
                                <td className="category-name-cell">{template.name}</td>
                                <td className="outreach-muted">{template.category?.name || '—'}</td>
                                <td className="outreach-muted">{template.subject || '—'}</td>
                                <td>
                                    <span className={`category-status ${template.is_public ? 'category-status-active' : 'category-status-inactive'}`}>
                                        {template.is_public ? 'Public' : 'Private'}
                                    </span>
                                </td>
                                <td className="outreach-muted">{template.owner?.email || '—'}</td>
                                <td>
                                    {this.canManage(template) ? (
                                        <div className="category-row-actions">
                                            <button className="outreach-edit-btn" onClick={() => navigator().navigate(`/outreach/templates/${template.id}/edit`)}>
                                                Edit
                                            </button>
                                            <button
                                                className="outreach-edit-btn category-delete-btn"
                                                onClick={() => this.handleDelete(template)}
                                                disabled={deletingId === template.id}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="outreach-muted">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    render(): ReactNode {
        const { templates, categories, loading, error, needsAuth, categoryFilter } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Email Templates</h1>
                        <p className="outreach-subtitle">
                            {templates.length} template{templates.length === 1 ? '' : 's'}
                        </p>
                    </div>
                    

                    <div className="outreach-toolbar">
                        <select className="outreach-status-select" value={categoryFilter} onChange={this.handleCategoryFilterChange}>
                            <option value="">All categories</option>
                            <option value="none">Uncategorized</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <button className="outreach-add-btn" onClick={() => navigator().navigate('/outreach/templates/add')}>
                        + Add Template
                    </button>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to manage templates.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadTemplates}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !error && !needsAuth && templates.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No templates yet.
                    </div>
                )}

                {!loading && !error && !needsAuth && templates.length > 0 && this.renderTable()}
            </div>
        )
    }
}

export { TemplatesPage }
